import React, { useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import {connect} from 'react-redux';
import {compose} from 'redux';
import colors from '../../util/colors';
import * as fonts from '../../util/fonts';
import {Icons} from '../../util/icons';
import {trackModalAction} from '../../util/appInsights';
import Header1 from '../headers/header1';
import Icon from '../icon';
import {Column, Row} from '../layout';
import './styles.scss';

interface ModalTemplateProps {
  modalId?: string;
  disableTelemetry?: boolean;
  type?: 'error' | 'success';
  inputType?: 'confirm' | 'option' | 'noInput';
  size?: 'sm' | 'lg' | 'xl';
  children: any;
  show: boolean;
  onHide(): void;
  cross?: boolean;
  onSubmit?(): void;
  onCancel?(): void;
  hideFooter?: boolean;
  title?: string;
  subTitle?: string;
  className?: string;
  contentClassName?: string;
  buStyles: any;
  centered?: boolean;
  titleStyle?: any;
  lockClickOutside?: boolean;
}

function ModalTemplate({
  modalId,
  disableTelemetry = false,
  type,
  children,
  show,
  onHide,
  cross = false,
  onSubmit,
  onCancel,
  hideFooter = false,
  title,
  subTitle,
  inputType = 'confirm',
  size,
  className,
  titleStyle,
  contentClassName,
  buStyles,
  lockClickOutside,
  centered = true,
}: ModalTemplateProps) {
  const modalIdentifier = modalId || title || subTitle || (type ? `modal_${type}` : 'modal');

  const prevShowRef = useRef(false);
  useEffect(() => {
    if (show && !prevShowRef.current && !disableTelemetry) {
      trackModalAction(modalIdentifier, 'open', {
        type: type || 'default',
        inputType,
      });
    }
    prevShowRef.current = show;
  }, [show, modalIdentifier, disableTelemetry, type, inputType]);

  const handleCrossClose = () => {
    if (!disableTelemetry) {
      trackModalAction(modalIdentifier, 'close_cross');
    }
    onHide();
  };

  const handleBackdropHide = () => {
    if (lockClickOutside) return;
    if (!disableTelemetry) {
      trackModalAction(modalIdentifier, 'close_backdrop');
    }
    onHide();
  };

  const handleConfirmSubmit = () => {
    if (!disableTelemetry) {
      trackModalAction(modalIdentifier, 'submit_confirm');
    }
    if (onSubmit) {
      onSubmit();
    }
    onHide();
  };

  const handleOptionYes = () => {
    if (!disableTelemetry) {
      trackModalAction(modalIdentifier, 'submit_option_yes');
    }
    if (onSubmit) {
      onSubmit();
    }
    onHide();
  };

  const handleOptionNo = () => {
    if (!disableTelemetry) {
      trackModalAction(modalIdentifier, 'cancel_option_no');
    }
    if (onCancel) {
      onCancel();
    }
    onHide();
  };

  let icon: any;
  let classIcon: any;
  let classCross: any;

  switch (type) {
    case 'error': {
      icon = Icons.ALERT_PROBLEM;
      classIcon = 'modal-header-icon-error';
      break;
    }
    case 'success': {
      icon = Icons.SUCCESS;
      classIcon = 'modal-header-icon';
      break;
    }
    default: {
      break;
    }
  }

  const renderFooter = () => {
    switch (inputType) {
      case 'confirm':
        return (
          <Modal.Footer
            className="modal-footer"
            onClick={handleConfirmSubmit}>
            Ok
          </Modal.Footer>
        );
      case 'option':
        return (
          <Modal.Footer className="modal-footer-option">
            <Row className="modal-option-row">
              <Column
                className="modal-option-column"
                onClick={handleOptionYes}>
                Sim
              </Column>
              <div className="vertical-row"></div>
              <Column
                className="modal-option-column"
                onClick={handleOptionNo}>
                Não
              </Column>
            </Row>
          </Modal.Footer>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      show={show}
      centered={centered}
      onHide={handleBackdropHide}
      className={className}
      contentClassName={contentClassName}
      size={size}
      animation={false} // Disable animation because it was causing a warning in the console
      >
      <Column className="modal-container">
        <div
          style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
          {(title || subTitle) && (
            <Row className="modal-container-header-title">
              <Column>
                {title && (
                  <Header1 className="title-header-modal" style={titleStyle}>
                    {title}
                  </Header1>
                )}
              </Column>
            </Row>
          )}
          <Icon
            className="modal-cross-close svg-close-button custom-close-button"
            name={Icons.CLOSE}
            width={'26'}
            height={'26'}
            changeViewbox={true}
            strokeWidth={'0.5'}
            onClick={handleCrossClose}
            display={cross ? '' : 'none'}
            color={buStyles?.primaryColor || colors.blue}
          />
        </div>
        {classIcon && (
          <Row>
            <Icon
              className={classIcon}
              name={icon}
              width={'28'}
              height={'28'}
            />
          </Row>
        )}
        <div
          style={{
            ...fonts.M,
            color: colors.black,
            display: 'flex',
            justifyContent: 'center',
          }}>
          {subTitle}
        </div>
        <Column className="modal-container-information">{children}</Column>
      </Column>
      {!hideFooter && renderFooter()}
    </Modal>
  );
}

ModalTemplate.defaultProps = {
  titleStyle: {color: colors.black, fontWeight: 'bold'},
};

const mapStateToProps = ({globalStyles}: any) => ({
  buStyles: globalStyles.value,
});

export default compose(connect(mapStateToProps)(ModalTemplate));
